import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Timer, Brain, Heart, Shield, Users, Zap, ArrowRight, ArrowLeft, RefreshCw, Clock, Target, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const tocItems = [
  { id: "burnout-is-overload", title: "Burnout Is About Overload" },
  { id: "continuous-studying-stress", title: "Why Continuous Studying Increases Stress" },
  { id: "permission-to-rest", title: "Pomodoro Gives Permission to Rest" },
  { id: "short-breaks-prevent-burnout", title: "Short Breaks Prevent Burnout" },
  { id: "emotion-regulation", title: "Pomodoro Helps Regulate Emotions" },
  { id: "contained-effort", title: "Why Contained Effort Reduces Stress" },
  { id: "studying-with-others", title: "Studying With Others Reduces Stress" },
  { id: "csw-burnout", title: "Where CSW Fits In" },
  { id: "faq", title: "FAQ" },
];

const AvoidBurnout = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does the Pomodoro Technique help prevent burnout?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pomodoro prevents burnout by interrupting the cycle of chronic cognitive overload. Instead of long, unstructured study sessions where stress compounds, Pomodoro provides scheduled breaks that give your nervous system time to recover before fatigue accumulates."
        }
      },
      {
        "@type": "Question",
        "name": "Why does continuous studying increase stress?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "When you study without clear stopping points, your brain stays in a constant state of effort. Research shows that prolonged cognitive effort without breaks increases stress hormones, reduces emotional regulation, and lowers learning efficiency."
        }
      },
      {
        "@type": "Question",
        "name": "What is 'psychological permission' in the Pomodoro Technique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Psychological permission means you don't have to decide whether to stop or feel guilty for resting — breaks are scheduled. Research shows that planned, predictable breaks reduce anxiety more effectively than unplanned rest because your nervous system can relax when rest is expected."
        }
      },
      {
        "@type": "Question",
        "name": "How do short breaks prevent burnout?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Burnout builds slowly, not suddenly. Studies on attention restoration show that even brief mental breaks restore focus, lower fatigue, and stabilize mood. Pomodoro's short breaks act as micro-recoveries, keeping stress from accumulating across long study sessions."
        }
      },
      {
        "@type": "Question",
        "name": "Why does studying with others reduce stress?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Burnout thrives in isolation. Research on shared work environments shows that co-presence lowers stress and increases persistence, even when people work silently. Study streams and virtual study rooms provide this social presence without adding pressure."
        }
      },
      {
        "@type": "Question",
        "name": "Why does bounded effort feel less stressful?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Students often burn out because effort feels endless. Pomodoro reframes studying as 'I'm doing this for 25 minutes — not forever.' Research shows that bounded tasks feel less stressful than open-ended ones, even when total effort is similar."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Studying Without Burning Out: How Pomodoro Manages Stress",
    "description": "Learn how the Pomodoro Technique helps students manage stress and prevent burnout through scheduled breaks, psychological permission to rest, and bounded effort.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/avoid-burnout/"
    },
    "keywords": ["pomodoro technique", "burnout prevention", "stress management", "study stress", "mental fatigue", "recovery", "breaks", "student wellness", "study stream", "online workspace", "virtual study room", "focus timer", "productivity", "sustainable studying", "cognitive overload", "study with me", "online study platform"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Studying Without Burning Out: How Pomodoro Manages Stress | CSW",
    "description": "Learn how the Pomodoro Technique helps students manage stress and prevent burnout through scheduled breaks and bounded effort.",
    "url": "https://csw.live/pomodoro-technique/avoid-burnout/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Burnout Prevention"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Burnout Prevention" },
      { "@type": "Thing", "name": "Stress Management" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Student Wellness" },
      { "@type": "Thing", "name": "Mental Fatigue" },
      { "@type": "Thing", "name": "Cognitive Overload" },
      { "@type": "Thing", "name": "Sustainable Studying" },
      { "@type": "Thing", "name": "Recovery Breaks" }
    ],
    "keywords": "pomodoro technique, burnout prevention, stress management, study stress, mental fatigue, recovery, study stream, online workspace, virtual study room, focus timer, student wellness, sustainable studying"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Avoid Burnout",
        "item": "https://csw.live/pomodoro-technique/avoid-burnout/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "Pomodoro 101: What It Really Is",
      path: "/pomodoro-technique/pomodoro-101/",
      description: "Clear up common misconceptions about the technique"
    },
    {
      title: "The Science of Focus",
      path: "/pomodoro-technique/science-of-focus/",
      description: "Why 25 minutes is enough (most of the time)"
    },
    {
      title: "Pomodoro vs. Procrastination",
      path: "/pomodoro-technique/pomodoro-vs-procrastination/",
      description: "How short sessions make starting easier"
    },
    {
      title: "Building Better Study Habits",
      path: "/pomodoro-technique/study-habits/",
      description: "How Pomodoro creates lasting routines"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="Studying Without Burning Out: How Pomodoro Manages Stress | CSW"
        description="Learn how the Pomodoro Technique helps students manage stress and prevent burnout through scheduled breaks, psychological permission to rest, and bounded effort."
        canonicalUrl="/pomodoro-technique/avoid-burnout/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">Avoid Burnout</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                <span>Stress Management</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Studying Without{" "}
                <span className="text-primary">Burning Out</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                How the Pomodoro Technique helps you manage stress (not just time).
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-24 relative">
          <div className="container mx-auto px-4">
            <div className="flex gap-12 max-w-6xl mx-auto">
              {/* Sidebar TOC - Desktop */}
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <BlogTableOfContents items={tocItems} />
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 max-w-3xl">
                {/* Table of Contents - Mobile */}
                <div className="lg:hidden mb-12">
                  <BlogTableOfContents items={tocItems} />
                </div>

                {/* Lead Paragraph */}
                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                  Burnout doesn't happen because you're weak. It happens because you're <strong>overloaded</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Most students experience it at some point: the mental exhaustion, irritability, the feeling of being "done" even before starting.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  The Pomodoro Technique is usually framed as a focus tool. But for many students, its real value is something else — it's a <strong>stress management system</strong>.
                </p>

                {/* Burnout Is About Overload */}
                <div id="burnout-is-overload" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Burnout Isn't About Weakness — It's About Overload
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Academic burnout usually shows up as:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>mental exhaustion</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>irritability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>lack of motivation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>feeling "done" even before starting</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research shows burnout is closely tied to <strong>chronic cognitive and emotional overload</strong>, not lack of effort (
                  <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4911781/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Maslach & Leiter, 2016</a>).
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  When students push themselves for long, unstructured study sessions, stress compounds — and recovery gets delayed. Pomodoro interrupts that cycle by enforcing <Link to="/pomodoro-technique/why-breaks-matter/" className="text-primary hover:underline">scheduled breaks</Link> that give your nervous system time to recover.
                </p>

                {/* Why Continuous Studying Increases Stress */}
                <div id="continuous-studying-stress" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Continuous Studying Increases Stress
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  When you study without clear stopping points, your brain stays in a constant state of effort.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Studies on mental fatigue show that prolonged cognitive effort without breaks (
                  <a href="https://psycnet.apa.org/record/2013-02636-000" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Hockey, 2013</a>):
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>increases stress hormones</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>reduces emotional regulation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>lowers learning efficiency</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    This is why "studying all day" often feels productive at first — and awful later.
                  </p>
                </div>

                {/* Pomodoro Gives Permission to Rest */}
                <div id="permission-to-rest" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Pomodoro Gives Your Brain Permission to Rest
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  One of the most underrated benefits of Pomodoro is <strong>psychological permission</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  You don't have to decide whether to stop.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  You don't have to feel guilty for resting.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  <strong>Breaks are scheduled.</strong>
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on stress and recovery shows that <strong>planned, predictable breaks</strong> reduce anxiety more effectively than unplanned rest (
                  <a href="https://journals.sagepub.com/doi/10.1177/0018726707081739" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Sonnentag & Fritz, 2007</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    When rest is expected, your nervous system relaxes.
                  </p>
                </div>

                {/* Short Breaks Prevent Burnout */}
                <div id="short-breaks-prevent-burnout" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Short Breaks Prevent Burnout Before It Starts
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Burnout builds slowly — not suddenly.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Studies on attention restoration show that even brief mental breaks (
                  <a href="https://www.sciencedirect.com/science/article/pii/027249449500002Y" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Kaplan, 1995</a>):
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>restore focus</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>lower fatigue</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>stabilize mood</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Pomodoro's short breaks act as <strong>micro-recoveries</strong>, keeping stress from accumulating across long study sessions.
                  </p>
                </div>

                {/* Pomodoro Helps Regulate Emotions */}
                <div id="emotion-regulation" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Pomodoro Helps Regulate Emotions During Studying
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Stress isn't just physical — it's emotional.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on emotion regulation shows that structured routines help people manage stress by reducing uncertainty and perceived overload (
                  <a href="https://psycnet.apa.org/record/2015-22785-001" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Gross, 2015</a>).
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>sets clear expectations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>removes endless time pressure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>makes effort feel finite</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    That emotional clarity matters.
                  </p>
                </div>

                {/* Why Contained Effort Reduces Stress */}
                <div id="contained-effort" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Burnout Decreases When Effort Feels "Contained"
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Students often burn out because effort feels endless.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro reframes studying as:
                </p>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-8">
                  <p className="text-xl font-display font-semibold text-foreground text-center m-0">
                    "I'm doing this for 25 minutes — not forever."
                  </p>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on workload perception shows that <strong>bounded tasks feel less stressful</strong> than open-ended ones, even when total effort is similar (
                  <a href="https://psycnet.apa.org/record/1998-10683-001" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Baumeister et al., 1998</a>).
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Containment reduces stress.
                  </p>
                </div>

                {/* Studying With Others */}
                <div id="studying-with-others" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Studying With Others Reduces Stress Even More
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Burnout thrives in isolation.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on shared work environments shows that <strong>co-presence lowers stress and increases persistence</strong>, even when people work silently (
                  <a href="https://www.smithsonianmag.com/science-nature/can-virtual-coworking-platforms-make-us-more-productive-180983190/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Smithsonian, 2023</a>).
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This is why Pomodoro feels easier in:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>study streams</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>virtual study rooms</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>shared focus sessions</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    You're not carrying the workload alone.
                  </p>
                </div>

                {/* Where CSW Fits In */}
                <div id="csw-burnout" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Where CSW Fits Into Stress-Free Studying
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This is exactly where <strong>csw.live</strong> comes in.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  CSW provides:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <Timer className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>Pomodoro-based study sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>silent camera-on rooms</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <RefreshCw className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>scheduled breaks with clear boundaries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>social presence without pressure</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Instead of grinding until burnout, CSW creates an environment where rest is built in — and studying becomes sustainable.
                </p>

                {/* Final Takeaway */}
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 mb-12">
                  <h3 className="text-xl font-display font-bold mb-4">Final Takeaway</h3>
                  <p className="text-lg font-body text-foreground/90 mb-0">
                    Burnout doesn't have to be the cost of academic success. Pomodoro protects your energy by scheduling recovery, containing effort, and giving you permission to rest. When you add shared study environments like CSW, stress drops even further — because you're not doing it alone.
                  </p>
                </div>

                {/* FAQ Section */}
                <div id="faq" className="bg-card border border-border rounded-2xl p-8 mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight">
                        Frequently Asked Questions
                      </h2>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {faqSchema.mainEntity.map((faq, index) => (
                      <div key={index} className="border-b border-border pb-6 last:border-0 last:pb-0">
                        <h3 className="text-lg font-display font-semibold mb-2">{faq.name}</h3>
                        <p className="text-foreground/80 font-body">{faq.acceptedAnswer.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center py-8 border-t border-border">
                  <Link 
                    to="/pomodoro-technique/" 
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Pomodoro Technique</span>
                  </Link>
                  <Link 
                    to="/study-timer/" 
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full hover:bg-primary/90 transition-colors"
                  >
                    <span>Try the Pomodoro Timer</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Related Articles */}
                <div className="mt-16">
                  <h2 className="text-2xl font-display font-bold mb-8">Related Articles</h2>
                  <div className="grid gap-4">
                    {relatedArticles.map((article, index) => (
                      <Link 
                        key={index}
                        to={article.path}
                        className="group p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">{article.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Study Stream Resources */}
                <div className="mt-12 p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl">
                  <h2 className="text-2xl font-display font-bold mb-3">Beat Burnout Inside a Study Stream</h2>
                  <p className="text-muted-foreground mb-6">
                    Solo Pomodoros + isolation = burnout. Body-double with others in a free <Link to="/study-stream/" className="text-primary hover:underline">Study Stream</Link> to keep recovery and rhythm in balance:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Link to="/study-stream/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Free 24/7 Study Stream to Beat Burnout</div>
                      <div className="text-sm text-muted-foreground">Sustainable Pomodoro sessions in a live study stream</div>
                    </Link>
                    <Link to="/study-stream/studying-alone-together/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Studying Alone, Together: Body Doubling for Burnout</div>
                      <div className="text-sm text-muted-foreground">How study streams stop isolation-fueled burnout</div>
                    </Link>
                    <Link to="/study-stream/why-studying-on-camera-helps/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Why Studying on Camera Helps You Focus Longer</div>
                      <div className="text-sm text-muted-foreground">Camera accountability without burnout</div>
                    </Link>
                    <Link to="/study-stream/productivity-hacks/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">10 Productivity Hacks from Study Streams</div>
                      <div className="text-sm text-muted-foreground">Maintain output, skip the burnout spiral</div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default AvoidBurnout;
